/* import __COLOCATED_TEMPLATE__ from './header.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type KnowledgeHubEditorService from 'embercom/services/knowledge-hub-editor-service';
import type SuggestionService from 'embercom/services/suggestion-service';
import type { ReviewableContent } from 'embercom/services/suggestion-service';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import { action } from '@ember/object';
import { schedule } from '@ember/runloop';
import type IntlService from 'ember-intl/services/intl';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import { ReviewType } from 'embercom/models/content-service/content-review-request';
import {
  humanReadableObjectNames,
  objectNames,
} from 'embercom/models/data/matching-system/matching-constants';
import { EntityType } from 'embercom/models/data/entity-types';
import type ContentReviewRequest from 'embercom/models/content-service/content-review-request';
import { Status } from 'embercom/models/data/content-service/content-review-statuses';
import { tracked } from '@glimmer/tracking';
import { timeout } from 'ember-concurrency';
import type { TaskGenerator } from 'ember-concurrency';
import { restartableTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { modifier } from 'ember-modifier';
import type ArticleContent from 'embercom/models/articles/article-content';
import safeWindowOpen from 'embercom/lib/safe-window-open';

interface HeaderArgs {
  showSidePanel: boolean;
  toggleSidePanel: () => void;
  activeContent: ReviewableContent;
  publishArticleContent: () => Promise<void>;
  isSaving: boolean;
  saveAsDraft: () => Promise<void>;
  onClose: () => void;
  showBackButton?: boolean;
  showCancelButton: boolean;
  reviewRequests: ContentReviewRequest[];
  onToggleFullScreen: () => void;
  showDrawerControls: boolean;
  onStopGenerating: () => void;
  generationInProgress: boolean;
  isPublishing: boolean;
  saveContentSnippet: () => Promise<void>;
  isSavingAsDraft: boolean;
  showSidePanelOpener?: boolean;
}

export default class Header extends Component<HeaderArgs> {
  closeSidePanelOnResize = true;

  @service declare knowledgeHubEditorService: KnowledgeHubEditorService;
  @service declare intl: IntlService;
  @service declare suggestionService: SuggestionService;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;
  @service declare appService: AppService;
  @service declare intercomConfirmService: IntercomConfirmService;

  @tracked generationStatusText: string;

  constructor(owner: any, args: HeaderArgs) {
    super(owner, args);

    this.generationStatusText = '';
  }

  refreshOnGenerationInProgress = modifier(
    (_: Element, [generationInProgress]: [boolean]) => {
      let taskInstance = taskFor(this.updateGenerationStatusText);
      if (generationInProgress) {
        taskInstance.perform();
      } else {
        this.generationStatusText = '';
        taskInstance.cancelAll();
      }
    },
    { eager: false },
  );

  get isContentSnippet() {
    return this.args.activeContent.entityType === EntityType.ContentSnippet;
  }

  get isArticleContent() {
    return this.args.activeContent.entityType === EntityType.ArticleContent;
  }

  get isContentSectionRelationship() {
    return this.args.activeContent.entityType === EntityType.ContentSectionRelationship;
  }

  get articleContent() {
    return this.args.activeContent as unknown as ArticleContent;
  }

  get isZendeskStandalone(): boolean {
    return this.isArticleContent && this.appService.app.hasStandalonePlatform('zendesk');
  }

  get isSalesforceStandalone(): boolean {
    return (
      this.isArticleContent &&
      this.appService.app.hasStandalonePlatform('salesforce') &&
      this.appService.app.canUseSalesforceArticleSynchroniser
    );
  }

  get hasZendeskExternalUrl(): boolean {
    return !!this.articleContent.externalUrl;
  }

  get hasSalesforceExternalUrl(): boolean {
    return !!this.articleContent.externalUrl;
  }

  get title() {
    let content = this.args.activeContent;
    let objectName = objectNames[content.entityType];
    let reviewType = this.args.reviewRequests.firstObject?.get('reviewType');

    if (reviewType === ReviewType.CREATE_CONTENT) {
      return this.intl.t(`knowledge-hub.content-editor.drawer-editor.titles.add-content`);
    } else if (reviewType === ReviewType.EDIT_CONTENT) {
      return this.intl.t(`knowledge-hub.content-editor.drawer-editor.titles.edit-${objectName}`);
    } else if (reviewType === ReviewType.DELETE_CONTENT) {
      return this.intl.t(`knowledge-hub.content-editor.drawer-editor.titles.delete-${objectName}`);
    } else if (reviewType === ReviewType.DUPLICATE_CONTENT) {
      return this.intl.t('knowledge-hub.content-editor.drawer-editor.titles.duplicate-content');
    } else if (reviewType === ReviewType.CONTRADICTING_CONTENT) {
      return this.intl.t('knowledge-hub.content-editor.drawer-editor.titles.contradicting-content');
    } else if (reviewType === ReviewType.UNDERPERFORMING_CONTENT) {
      return this.intl.t('knowledge-hub.content-editor.review.review-underperforming-title', {
        contentType: humanReadableObjectNames[content.entityType].toLowerCase(),
      });
    }

    return this.intl.t(
      `knowledge-hub.content-editor.drawer-editor.titles.fallback-title-${objectName}`,
    );
  }

  get isCreateContent() {
    return this.args.reviewRequests.firstObject?.get('reviewType') === ReviewType.CREATE_CONTENT;
  }

  get isUnderperformingContent() {
    return this.args.reviewRequests.firstObject?.isUnderperformingContent;
  }

  get isEditContent() {
    return this.args.reviewRequests.firstObject?.get('reviewType') === ReviewType.EDIT_CONTENT;
  }

  get isAnyPending() {
    return this.args.reviewRequests.any(
      (request) =>
        !request.isContradictingContent &&
        !request.isDuplicateContent &&
        request.get('status') === Status.Pending,
    );
  }

  get isAllApproved() {
    return this.args.reviewRequests.every((request) => request.get('status') === Status.Approved);
  }

  get isAllRejected() {
    return this.args.reviewRequests.every((request) => request.get('status') === Status.Rejected);
  }

  get isSaveAsDraftDisabled() {
    if (this.isArticleContent) {
      if (
        this.args.reviewRequests.any((request) => request.get('status') === Status.Approved) ||
        this.suggestionService.hasDirtyContent
      ) {
        return false;
      }
    }

    return true;
  }

  get showPublishButton() {
    return this.isArticleContent;
  }

  get hidePublishButtonTooltip() {
    if (this.isUnderperformingContent) {
      return true;
    }
    return !this.disablePublishButton;
  }

  get disablePublishButton() {
    // cannot publish if there are pending content review requests
    if (this.isAnyPending) {
      return true;
    }

    if (!this.isArticleContent) {
      return true;
    }

    if (this.isUnderperformingContent && this.suggestionService.hasDirtyContent) {
      return false;
    }

    if (
      this.isFromConDupeSuggestion &&
      (this.suggestionService.hasDirtyContent ||
        this.articleContent.publishingStatus !== 'published')
    ) {
      return false;
    }

    if (!this.isEditContent) {
      return true;
    }

    return this.isAllRejected && !this.suggestionService.hasDirtyContent;
  }

  get showSaveButton() {
    return (
      this.isContentSnippet &&
      (this.isEditContent || this.isUnderperformingContent || this.isFromConDupeSuggestion)
    );
  }

  get isFromConDupeSuggestion() {
    return !!this.knowledgeHubDrawerEditorService.fromConDupeSuggestion;
  }

  get showAutoSave() {
    return this.isContentSnippet && this.isCreateContent && this.args.isSaving;
  }

  get showSaveAsDraftButton() {
    // In Zendesk saving as draft would unpublish the current live article, so we don't want to allow that
    return this.isArticleContent && !this.appService.app.canUseStandalone;
  }

  get showSaveButtonTooltip() {
    if (this.isUnderperformingContent) {
      return false;
    }

    return this.isSaveButtonDisabled && this.isAnyPending;
  }

  get isSaveButtonDisabled() {
    if (this.isUnderperformingContent) {
      return !this.suggestionService.hasDirtyContent;
    }

    return this.args.isSaving || this.isAnyPending || !this.suggestionService.hasDirtyContent;
  }

  get showBackButton() {
    return this.args.showBackButton ?? false;
  }

  get showSidePanelOpener() {
    return this.args.showSidePanelOpener ?? true;
  }

  @action
  async handlePublishClick() {
    if (this.isZendeskStandalone) {
      let confirmed = await this.intercomConfirmService.confirm({
        title: this.intl.t(
          'knowledge-hub.content-editor.drawer-editor.review-mode-header.zendesk-publish-modal.title',
        ),
        body: this.intl.t(
          'knowledge-hub.content-editor.drawer-editor.review-mode-header.zendesk-publish-modal.message',
          {
            hasZendeskUrl: this.hasZendeskExternalUrl,
            zendeskUrl: this.articleContent.externalUrl,
            htmlSafe: true,
          },
        ) as string,
        confirmButtonText: this.intl.t(
          'knowledge-hub.content-editor.drawer-editor.review-mode-header.zendesk-publish-modal.publish',
        ),
        cancelButtonText: this.intl.t(
          'knowledge-hub.content-editor.drawer-editor.review-mode-header.zendesk-publish-modal.cancel',
        ),
      });
      if (!confirmed) {
        return;
      }
    }

    if (this.isSalesforceStandalone) {
      let confirmed = await this.intercomConfirmService.confirm({
        title: this.intl.t(
          'knowledge-hub.content-editor.drawer-editor.review-mode-header.salesforce-publish-modal.title',
        ),
        body: this.intl.t(
          'knowledge-hub.content-editor.drawer-editor.review-mode-header.salesforce-publish-modal.message',
          {
            hasSalesforceUrl: this.hasSalesforceExternalUrl,
            salesforceUrl: this.articleContent.externalUrl,
            htmlSafe: true,
          },
        ) as string,
        confirmButtonText: this.intl.t(
          'knowledge-hub.content-editor.drawer-editor.review-mode-header.salesforce-publish-modal.publish',
        ),
        cancelButtonText: this.intl.t(
          'knowledge-hub.content-editor.drawer-editor.review-mode-header.salesforce-publish-modal.cancel',
        ),
      });
      if (!confirmed) {
        return;
      }
    }

    await this.args.publishArticleContent();

    if (this.isZendeskStandalone && this.hasZendeskExternalUrl) {
      safeWindowOpen(this.articleContent.externalUrl, '_blank');
    }

    if (this.isSalesforceStandalone && this.hasSalesforceExternalUrl) {
      safeWindowOpen(this.articleContent.externalUrl, '_blank');
    }
  }

  @action
  closeDrawer() {
    this.args.onClose();
  }

  @action
  async cancelReview() {
    await this.knowledgeHubDrawerEditorService.performContentAction('cancel');

    this.knowledgeHubDrawerEditorService.openViewDrawer({
      activeContentId: this.args.activeContent.entityId,
      activeContentType: objectNames[this.args.activeContent.entityType],
    });
  }

  @action closeSidePanel(header: HTMLElement) {
    let headerHasScroll = header.offsetWidth < header.scrollWidth;
    let closeSidePanel = headerHasScroll && this.closeSidePanelOnResize && this.args.showSidePanel;
    this.closeSidePanelOnResize = !headerHasScroll && this.args.showSidePanel;
    if (closeSidePanel) {
      // schedule the side panel to close after it finishes opening, to avoid a race condition
      schedule('afterRender', () => {
        this.args.toggleSidePanel();
      });
    }
  }

  @restartableTask
  *updateGenerationStatusText(): TaskGenerator<void> {
    let delayMs = 400;
    let statusMessages: string[] = [
      this.intl.t(
        'knowledge-hub.content-editor.review.suggestion-generation-status.reading-the-content',
      ),
      this.intl.t(
        'knowledge-hub.content-editor.review.suggestion-generation-status.finding-sections-to-edit',
      ),
      this.intl.t('knowledge-hub.content-editor.review.suggestion-generation-status.making-edits'),
      this.intl.t(
        'knowledge-hub.content-editor.review.suggestion-generation-status.validating-changes',
      ),
      this.intl.t('knowledge-hub.content-editor.review.suggestion-generation-status.wrapping-up'),
    ];
    let lastMessage = statusMessages.pop()!;

    for (let statusMessage of statusMessages) {
      this.generationStatusText = statusMessage;
      for (let i = 0; i < 12; i++) {
        yield timeout(delayMs);
        this.generationStatusText = statusMessage + '.'.repeat(i % 4);
      }
      yield timeout(delayMs);
    }

    // Keep showing the last message until the replacement is complete
    let i = 0;
    while (this.args.generationInProgress) {
      this.generationStatusText = lastMessage + '.'.repeat(i);
      yield timeout(delayMs);
      i = (i + 1) % 4;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::ContentReview::Header': typeof Header;
    'knowledge-hub/content-editor/content-review/header': typeof Header;
  }
}
